import { FormDatePicker } from "components/DatePicker/SingleDatePicker";
import PaymentMethodDropdown from "components/dropdowns/reactselect/PaymentMethodDropdown";
import { DetailSectionTemplate } from "components/MainSectionTemplate/DetailSectionTemplate";
import { RenderEditOptions } from "components/MainSectionTemplate/types";
import { CompanySmallDetails, InvoiceSmallDetails } from "components/SmallDetails";
import OptionalTextInput from "components/ValidatedFields/OptionalTextInput";
import RequiredNumberInput from "components/ValidatedFields/RequiredNumberInput";
import moment from "moment";
import React, { useState } from "react";
import { formatCurrency, formatShortDate } from "utilities";

interface PaymentDetailSecitonProps {
  payment: server.dto.Payment
}

export const PaymentDetailSection = (props: PaymentDetailSecitonProps) => {
  const [payment, setPayment] = useState<server.dto.Payment>(props.payment);

  const onValueChanged = (key: string, value: any) => {
    if (key === "receivedOn") {
      setPayment({ ...payment, receivedOn: value });
    } else if (key === "description") {
      setPayment({ ...payment, description: value });
    } else if (key === "method") {
      setPayment({ ...payment, method: value });
    } else if (key === "amount") {
      setPayment({
        ...payment,
        amount: {
          ...payment.amount,
          amount: value,
        }
      });
    }
  }
  const getUpdatePaymentInstruction = (): server.dto.UpdatePaymentInstruction => ({
    paymentId: payment.paymentId,
    receivedOn: payment.receivedOn,
    method: payment.method.value,
    description: payment.description,
    amount: payment.amount.amount,
  });

  return <DetailSectionTemplate
    isInPageEditable={payment.permissions.canModify}
    onValueChanged={onValueChanged}
    sections={[{
      key: "paymentDetails",
      type: "regular",
      title: RESX.Payment.resxPayment,
      fields: [{
        key: "receivedOn",
        label: RESX.GeneralLabels.ReceivedOn,
        render: () => <div>
          {formatShortDate(moment.utc(payment.receivedOn))}
        </div>,
        inPageEdit: {
          render: ({ value, onChange, submit, cancel }: RenderEditOptions<moment.Moment>) => <FormDatePicker
            validatorKey="receivedOn"
            focusOnMount
            value={value}
            onChange={(v, fromCalendar) => onChange(v, fromCalendar)}
            minDate={moment.utc(serverReference.constants.payment.minYearDate)}
            maxDate={moment.utc(serverReference.constants.payment.maxYearDate)}
            onEsc={() => cancel()}
            onEnter={() => submit()}
          />,
          type: "confirmOrCancelButtonInline",
          initialValue: moment.utc(payment.receivedOn),
          submitChange: (receivedOn: moment.Moment) => window.CoreApi.Payment.Update({
            ...getUpdatePaymentInstruction(),
            receivedOn: receivedOn.toDate(),
          }),
        },
      }, {
        key: "description",
        label: RESX.GeneralLabels.Description,
        render: () => <div>{payment.description}</div>,
        inPageEdit: {
          render: ({ value, onChange, submit, cancel }: RenderEditOptions<string>) => <OptionalTextInput
            focusOnMount
            maxLength={serverReference.constants.payment.maxDescriptionLength}
            value={value}
            onChange={(title) => { onChange(title); }}
            onEnter={() => submit()}
            onEsc={() => cancel()}
          />,
          fullWidth: true,
          type: "confirmOrCancelButton",
          initialValue: payment.description ?? "",
          submitChange: (description: string) => window.CoreApi.Payment.Update({
            ...getUpdatePaymentInstruction(),
            description,
          }),
        },
        isEmpty: !payment.description,
      }, {
        key: "method",
        label: RESX.Payment.PaymentMethod,
        render: () => <div>{payment.method.text}</div>,
        inPageEdit: {
          render: ({ value, onChange, cancel }: RenderEditOptions<server.dto.PaymentMethod>) => <PaymentMethodDropdown
            value={value}
            onChange={(v: server.dto.PaymentMethod) => onChange(v, true)}
            onEsc={() => cancel()}
            config={{
              focusOnMount: true,
              openMenuOnFocus: true,
            }}
          />,
          fullWidth: true,
          type: "custom",
          initialValue: payment.method,
          submitChange: (method: server.dto.PaymentMethod) => window.CoreApi.Payment.Update({
            ...getUpdatePaymentInstruction(),
            method: method.value,
          }),
        },
      }, {
        key: "amount",
        label: RESX.Financial.Amount,
        render: () => <div>{formatCurrency(payment.amount.amount, payment.amount.currency, serverReference.culture.uiLocale)}</div>,
        inPageEdit: {
          render: ({ value, onChange, cancel }: RenderEditOptions<number>) => <RequiredNumberInput
            autoFocus
            width={90}
            validatorKey="amount"
            value={value}
            onChange={(v: number) => onChange(v)}
            onEnterPressed={(v: number) => onChange(v, true)}
            onEscPressed={() => cancel()}
            decimalPoints={2}
            labelContent={payment.amount.currency.toUpperCase()}
            canBeNegative
          />,
          type: "confirmOrCancelButton",
          initialValue: payment.amount.amount,
          submitChange: (amount: number) => window.CoreApi.Payment.Update({
            ...getUpdatePaymentInstruction(),
            amount,
          }),
        },
      }, {
        key: "receivedFrom",
        label: RESX.Payment.ReceivedFrom,
        render: () => <CompanySmallDetails company={payment.receivedFrom} layout="multiLine" />,
      }, {
        key: "assignedToInvoice",
        label: RESX.Payment.AssignedToInvoice,
        render: () => <InvoiceSmallDetails invoice={payment.assignedToInvoice} />,
      }],
    }]}
  />
}